import jsonp from 'jsonp';
import axios from 'axios';

/**
 * Create groups of objects with matching keys
 * @param list Array of objects
 * @param groupKey Key in object to group items on, or a function that returns a key or an empty string.
 * @param groupListKey Key for array with grouped items
 * @param returnObj Function that returns object for group-parents
 */
function groupByObjectValue(list, groupKey, groupListKey = 'items', returnObj) {
    const createdGroups = new Map();
    let getGroupKey;
    if (typeof groupKey === 'string') {
        getGroupKey = (obj) => obj[groupKey];
    }
    else if (typeof groupKey === 'function') {
        getGroupKey = (obj) => groupKey(obj);
    }
    else {
        console.debug('Invalid groupKey provided. Should be a string or a function', groupKey);
        return list;
    }
    // return object
    let getGroupObj = (obj, name) => ({
        type: 'group',
        name,
        [groupListKey]: [obj],
    });
    if (typeof returnObj === 'function') {
        getGroupObj = (obj, name) => returnObj(obj, name);
    }
    return list.reduce((acc, cur) => {
        const curGroupKey = getGroupKey(cur);
        // It item has group value
        if (curGroupKey) {
            if (createdGroups.has(curGroupKey)) {
                // Group exists, add to it.
                const existingGroupIndex = createdGroups.get(curGroupKey);
                acc[existingGroupIndex][groupListKey].push(cur);
            }
            else {
                // Create new group
                createdGroups.set(curGroupKey, acc.length);
                acc.push(getGroupObj(cur, curGroupKey));
            }
        }
        else {
            acc.push(cur);
        }
        return acc;
    }, []);
}

function validateProps(props, data) {
    Object.entries(props).forEach(([key, value]) => {
        let dataType = typeof data[key];
        if (dataType === 'object' && Array.isArray(data[key])) {
            dataType = 'array';
        }
        if (value.required && dataType === 'undefined') {
            throw new Error(`Missing required value: ${key}`);
        }
        else if (Array.isArray(value.types) && dataType !== 'undefined') {
            if (!value.types.includes(dataType)) {
                throw new Error(`Incorrect value type for: ${key}. Expected: ${value.types.join(', ')}, got ${dataType}`);
            }
        }
    });
    return true;
}

/**
 * Sort an array of objects based on a specific key
 * @param {Object.<string,any>} arg
 * @property arg.data Array of objects to sort
 * @property arg.sortKey Key to sort on
 * @property arg.direction Sort direction
 */
function sortBy({ data, sortKey, direction = 'AZ' }) {
    const props = {
        data: {
            required: true,
            types: ['array'],
        },
        sortKey: {
            required: true,
            types: ['string'],
        },
        direction: {
            required: true,
            types: ['string'],
        },
    };
    validateProps(props, { data, sortKey, direction });
    const inverter = direction === 'ZA' ? 1 : -1;
    data.sort((a, b) => (a[sortKey] < b[sortKey] ? 1 * inverter : -1 * inverter));
    return data;
}

/**
 * Sort an array of objects based on the order of items in another array
 * @param data Array of objects to sort
 * @param sortKey Key to sort on
 * @param order List of unique keys in pre-defined order
 */
function sortByArray({ data, sortKey, order }) {
    const props = {
        data: {
            required: true,
            types: ['array'],
        },
        sortKey: {
            required: true,
            types: ['string'],
        },
        order: {
            required: true,
            types: ['array'],
        },
    };
    validateProps(props, { data, sortKey, order });
    const indexes = order.reduce((acc, cur, index) => {
        acc[cur] = index;
        return acc;
    }, {});
    function getIndexPosition(val) {
        // Sort missing indexes at the end of the list
        if (val && typeof indexes[val] === 'number') {
            return indexes[val];
        }
        return 999;
    }
    return data.sort((a, b) => getIndexPosition(a[sortKey]) - getIndexPosition(b[sortKey]));
}

/**
 * checks if target element is in the provided element
 * @namespace Browser
 * @param element - container
 * @param event - click event
 */
function containsTargetElement(element, event) {
    return element.contains(event.target);
}

/**
 * Selects contents of element (focus with select)
 * @namespace Browser
 * @param container
 * @param selector - css selector to look for in container
 */
function select(container, selector) {
    if (container && selector) {
        const el = container.querySelector(selector);
        if (el) {
            el.focus();
            el.select();
        }
    }
}
/**
 * Sets focus on first element in container with valid tabindex
 * @namespace Browser
 * @param container
 */
function focusFirstTabIndex(container) {
    if (container) {
        const el = container.querySelectorAll('[tabindex]');
        const firstValidEl = Array.from(el).find((it) => it.getAttribute('tabindex') !== '-1');
        if (firstValidEl) {
            firstValidEl.focus();
        }
    }
}

class Mailchimp {
    constructor({ dataCenter, userID, listID }) {
        const props = {
            dataCenter: {
                required: true,
                types: ['string'],
            },
            userID: {
                required: true,
                types: ['string'],
            },
            listID: {
                required: true,
                types: ['string'],
            },
        };
        validateProps(props, { dataCenter, userID, listID });
        this.dataCenter = dataCenter;
        this.userID = userID;
        this.listID = listID;
    }
    subscribeUrl(params = {}) {
        const queryParams = {
            u: this.userID,
            id: this.listID,
            ...params,
        };
        let query = new URLSearchParams(queryParams).toString();
        if (query) {
            query = `?${query}`;
        }
        return `https://name.${this.dataCenter}.list-manage.com/subscribe/post-json${query}`;
    }
    /* istanbul ignore next */
    subscribe({ ...fields }) {
        return new Promise((resolve, reject) => {
            const url = this.subscribeUrl(fields);
            jsonp(url, { param: 'c' }, (err, res) => {
                if (err) {
                    reject(err);
                }
                else {
                    resolve(res);
                }
            });
        });
    }
}

class ActiveCampaign {
    constructor({ apiURL, apiToken, endpoint = 'contacts' }) {
        const props = {
            apiURL: {
                required: true,
                types: ['string'],
            },
            apiToken: {
                required: true,
                types: ['string'],
            },
            endpoint: {
                required: true,
                types: ['string'],
            },
        };
        validateProps(props, { apiURL, apiToken, endpoint });
        this.url = apiURL;
        this.token = apiToken;
        this.endpoint = endpoint;
    }
    getData(fields) {
        const defaultFields = ['email', 'firstName', 'lastName', 'phone'];
        return Object.entries(fields).reduce((acc, [key, value]) => {
            if (defaultFields.includes(key)) {
                acc[key] = value;
            }
            else {
                acc.fieldValues.push({
                    field: key,
                    value,
                });
            }
            return acc;
        }, {
            fieldValues: [],
        });
    }
    validateData(fields) {
        return !!fields.email;
    }
    /* istanbul ignore next */
    subscribe(fields) {
        return new Promise((resolve, reject) => {
            if (!this.validateData(fields)) {
                reject(new Error('email is required'));
            }
            const data = { contact: this.getData(fields) };
            axios({
                method: 'POST',
                headers: {
                    'Api-Token': this.token,
                    'target-url': `${this.url}/${this.endpoint}/`,
                },
                url: 'https://europe-west1-blixem.cloudfunctions.net/corsprox',
                data,
            })
                .then((r) => {
                if (r.status === 201) {
                    // created
                    resolve(r.data);
                }
                else {
                    reject(new Error(`Invalid responsecode: ${r.status}`));
                }
            })
                .catch((error) => {
                // Email exists, allow this
                if (error.response && error.response.status === 422) {
                    resolve(data);
                }
                else {
                    reject(error);
                }
            });
        });
    }
}

/**
 * Wrap marked test in (html)-tags
 * @namespace Strings
 * @param val text to replace in
 * @param splitPart text to split on
 * @param startString inserted before split-text. Start tag
 * @param endString inserted after split-text. End tag
 */
function contrastText(val, splitPart = '==', startString = '<strong>', endString = '</strong>') {
    if (typeof val === 'string') {
        const parts = val.split(splitPart);
        return parts
            .map((part, index) => {
            if (index % 2 === 0) {
                return part;
            }
            return `${startString}${part}${endString}`;
        })
            .join('');
    }
    return val;
}

/**
 * Take the first letter of every word
 * @returns first letter of every word
 * @param text Input text
 * @param [max=2] Max amount of letters to return
 */
function getFirstLetterOfEveryWord(text, max = 2) {
    if (typeof text === 'string' && text) {
        return text
            .split(' ')
            .reduce((acc, cur) => (acc.length < max ? `${acc}${cur[0]}` : acc), '');
    }
    return '';
}

/**
 * Generate random password with 16 characters
 * @namespace Strings
 * @returns random string with 16 characters
 */
function generatePassword() {
    return `${Math.random().toString(36).slice(-8)}${Math.random().toString(36).slice(-8)}`;
}

/**
 * Get text from data-object in a certain language
 * @namespace Strings
 * @param obj data object to search in
 * @param key key to look for in object
 * @param locale current locale
 * @param fallback fallback text
 */
function getLocaleText(obj, key, locale, fallback = '') {
    if (obj && key && locale) {
        const localeKey = `${key}_${locale}`;
        if (typeof obj[localeKey] !== 'undefined') {
            return obj[localeKey];
        }
        return fallback;
    }
    return fallback;
}

/**
 * Convert linebreaks to <br />
 * @namespace Strings
 * @param val text containing linebreaks
 */
function nl2br(val = '') {
    if (typeof val === 'string') {
        return val.replace(/([^>\r\n]?)(\r\n|\n\r|\r|\n)/g, '$1<br />$2');
    }
    else {
        return val;
    }
}

/**
 * Replace bracketed variables in string
 * @namespace Strings
 * @param val url containing variables in [brackets]
 * @param data object with keys that match variable names
 */
function replaceBrackets(val, data) {
    const props = {
        val: {
            required: true,
            types: ['string'],
        },
        data: {
            required: true,
            types: ['object'],
        },
    };
    validateProps(props, { val, data });
    Object.keys(data).forEach((key) => {
        if (val.includes(`[${key}]`)) {
            val = val.replace(`[${key}]`, data[key]);
        }
    });
    return val;
}

/**
 * Generate score for password
 * @param pass password to check
 * @returns score, 0 - 100
 */
function scorePassword(pass) {
    let score = 0;
    if (!pass || typeof pass !== 'string')
        return score;
    // award every unique letter until 5 repetitions
    const letters = {};
    for (let i = 0; i < pass.length; i += 1) {
        letters[pass[i]] = (letters[pass[i]] || 0) + 1;
        score += 5.0 / letters[pass[i]];
    }
    // bonus points for mixing it up
    const variations = {
        digits: /\d/.test(pass),
        lower: /[a-z]/.test(pass),
        upper: /[A-Z]/.test(pass),
        nonWords: /\W/.test(pass),
    };
    let variationCount = 0;
    Object.values(variations).forEach((check) => {
        variationCount += check === true ? 1 : 0;
    });
    score += (variationCount - 1) * 10;
    return score;
}

/**
 * Prefix path with cdnpath, located in VUE_APP_CDNPATH env
 * @param path path to image
 */
function cdnUrl(path) {
    if (!path) {
        return undefined;
    }
    return `${process.env.VUE_APP_CDNPATH || 'VUE_APP_CDNPATH-not-defined/'}${path}`;
}

/**
 * Returns srcset path with images sizes
 * @param path path to image
 * @param widths list of image sizes
 */
function imageSizes(path, widths) {
    if (!path) {
        return '';
    }
    const extension = path.split('.').pop();
    return widths.reduce((acc, size, index) => {
        if (acc) {
            acc += ', ';
        }
        if (index === widths.length - 1) {
            // last size
            acc += `${path} ${size}w`;
        }
        else {
            acc += `${path}-${size}w.${extension} ${size}w`;
        }
        return acc;
    }, '');
}

export { ActiveCampaign, Mailchimp, cdnUrl, containsTargetElement, contrastText, focusFirstTabIndex, generatePassword, getFirstLetterOfEveryWord, getLocaleText, groupByObjectValue, imageSizes, nl2br, replaceBrackets, scorePassword, select, sortBy, sortByArray, validateProps };
//# sourceMappingURL=services.esm.js.map
